import { useState } from "react";
import { ChevronDown, Headphones, Volume2, BarChart3, Ear, Brain, TrendingUp, Activity } from "lucide-react";
import "@/components/LandingPage.css";

const APP_STORE_URL = "https://apps.apple.com/app/id6762517913";
const APP_SCREENSHOT = "https://customer-assets.emergentagent.com/job_noise-privacy-hub/artifacts/e1iwnb35_sc3.png";

const faqs = [
  {
    q: "Is this a real hearing test?",
    a: "Yes. Hearing Decibel Pro uses pure tone audiometry — the same methodology used in audiology clinics worldwide. You listen for calibrated tones at specific frequencies (250 Hz through 8,000 Hz) and tap when you hear them. The app plots your hearing thresholds on a standard audiogram, just like a clinical test would.",
  },
  {
    q: "How is this different from going to an audiologist?",
    a: "A clinical hearing evaluation uses calibrated equipment in a sound-isolated booth, administered by a licensed audiologist. Hearing Decibel Pro uses your iPhone and headphones in your environment. The test methodology is the same, but the conditions differ. This app is a screening tool — it can identify potential hearing loss, but it's not a substitute for a clinical evaluation.",
  },
  {
    q: "Do I need headphones?",
    a: "Yes. Wired or Bluetooth headphones are required for accurate results. Over-ear headphones or sealed in-ear earbuds work best. The app cannot test your hearing through the device speaker.",
  },
  {
    q: "Is it free?",
    a: "Your first hearing test is free. Hearing Pro unlocks unlimited tests, detailed tracking over time, hearing training exercises, and PDF export. Monthly, annual, and lifetime plans are available.",
  },
  {
    q: "How accurate is a phone hearing test?",
    a: "In a quiet environment with quality headphones, consumer pure tone audiometry correlates well with clinical results for screening purposes. However, without calibrated equipment and a sound booth, results may vary by several decibels. Use it as a screening tool — if results suggest hearing loss, see an audiologist.",
  },
  {
    q: "What is pure tone audiometry?",
    a: "Pure tone audiometry is the standard method audiologists use to measure hearing sensitivity. You listen for tones at specific frequencies (pitches) and volumes. The quietest level you can hear at each frequency is your hearing threshold. These thresholds are plotted on an audiogram — a graph that shows your hearing ability across the speech-relevant frequency range.",
  },
];

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="lp-faq-item" data-testid={`lp-faq-item-${index}`}>
      <button
        className={`lp-faq-q ${open ? "open" : ""}`}
        onClick={() => setOpen(!open)}
        data-testid={`lp-faq-toggle-${index}`}
        aria-expanded={open}
      >
        <span>{item.q}</span>
        <ChevronDown size={20} className={`lp-faq-icon ${open ? "rotated" : ""}`} />
      </button>
      {open && (
        <div className="lp-faq-a" data-testid={`lp-faq-answer-${index}`}>
          <p>{item.a}</p>
        </div>
      )}
    </div>
  );
};

const AppStoreBadge = ({ className = "" }) => {
  const handleClick = (e) => {
    if (typeof window.gtag_report_conversion === "function") {
      e.preventDefault();
      window.gtag_report_conversion(APP_STORE_URL);
    }
  };

  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`lp-appstore-badge ${className}`}
      data-testid="app-store-badge"
      aria-label="Download on the App Store"
      onClick={handleClick}
    >
      <img
        src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
        alt="Download on the App Store"
        className="lp-appstore-img"
      />
    </a>
  );
};

const LandingPage = () => {
  return (
    <div className="lp" data-testid="landing-page">
      {/* Hero */}
      <section className="lp-hero" data-testid="lp-hero">
        <div className="lp-container">
          <div className="lp-hero-layout">
            <div className="lp-hero-text">
              <h1 className="lp-h1" data-testid="lp-h1">
                Take a Pure Tone Hearing Test on Your iPhone
              </h1>
              <p className="lp-subhead" data-testid="lp-subhead">
                Clinical-grade pure tone audiometry, right on your phone. Test both ears
                across the full speech frequency range in under 5 minutes.
              </p>
              <p className="lp-free-badge" data-testid="lp-free-badge">
                Take your first test free
              </p>
              <div className="lp-hero-cta" data-testid="lp-hero-cta">
                <AppStoreBadge />
              </div>
              <p className="lp-trust" data-testid="lp-trust-signal">
                Requires headphones &bull; Takes 5 minutes &bull; iPhone only
              </p>
            </div>
            <div className="lp-hero-visual" data-testid="lp-hero-visual">
              <img
                src={APP_SCREENSHOT}
                alt="Hearing Decibel Pro audiogram results showing hearing test for both ears"
                className="lp-hero-screenshot"
                data-testid="lp-hero-screenshot"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="lp-section lp-section--alt" data-testid="lp-how-it-works">
        <div className="lp-container">
          <h2 className="lp-h2">How It Works</h2>
          <div className="lp-steps" data-testid="lp-steps">
            <div className="lp-step" data-testid="lp-step-1">
              <div className="lp-step-icon">
                <Headphones size={26} strokeWidth={1.5} />
              </div>
              <div className="lp-step-num">1</div>
              <p className="lp-step-text">Put on your headphones</p>
            </div>
            <div className="lp-step" data-testid="lp-step-2">
              <div className="lp-step-icon">
                <Volume2 size={26} strokeWidth={1.5} />
              </div>
              <div className="lp-step-num">2</div>
              <p className="lp-step-text">
                Tap when you hear each tone — both ears, 250 Hz to 8 kHz
              </p>
            </div>
            <div className="lp-step" data-testid="lp-step-3">
              <div className="lp-step-icon">
                <BarChart3 size={26} strokeWidth={1.5} />
              </div>
              <div className="lp-step-num">3</div>
              <p className="lp-step-text">Get your audiogram instantly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility */}
      <section className="lp-section" data-testid="lp-credibility">
        <div className="lp-container">
          <h2 className="lp-h2">What Is Pure Tone Audiometry?</h2>
          <div className="lp-prose" data-testid="lp-pta-explanation">
            <p>
              Pure tone audiometry is the standard method audiologists use to measure your
              hearing sensitivity. During the test, you listen for tones at specific
              frequencies — 250, 500, 1000, 2000, 4000, and 8000 Hz — which cover the range
              most important for understanding speech. At each frequency, the volume is
              gradually reduced until the quietest level you can hear is found. That level is
              your hearing threshold.
            </p>
            <p>
              Hearing Decibel Pro uses the same frequency range and threshold-finding
              methodology. Your results are plotted on a standard audiogram, the same format
              used by audiologists worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="lp-section lp-section--alt" data-testid="lp-what-you-get">
        <div className="lp-container">
          <h2 className="lp-h2">What You Get</h2>
          <div className="lp-features" data-testid="lp-features">
            <div className="lp-feature" data-testid="lp-feature-audiogram">
              <div className="lp-feature-icon">
                <Ear size={24} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="lp-feature-title">Personal Audiogram</h3>
                <p className="lp-feature-desc">
                  Full audiogram for both ears, showing your hearing thresholds at each
                  frequency
                </p>
              </div>
            </div>
            <div className="lp-feature" data-testid="lp-feature-thresholds">
              <div className="lp-feature-icon">
                <Activity size={24} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="lp-feature-title">Frequency-by-Frequency Results</h3>
                <p className="lp-feature-desc">
                  See your threshold at 250, 500, 1k, 2k, 4k, and 8k Hz — the full
                  speech-relevant range
                </p>
              </div>
            </div>
            <div className="lp-feature" data-testid="lp-feature-tracking">
              <div className="lp-feature-icon">
                <TrendingUp size={24} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="lp-feature-title">Track Hearing Over Time</h3>
                <p className="lp-feature-desc">
                  Save tests and compare results to detect changes in your hearing
                </p>
              </div>
            </div>
            <div className="lp-feature" data-testid="lp-feature-training">
              <div className="lp-feature-icon">
                <Brain size={24} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="lp-feature-title">Hearing Training Exercises</h3>
                <p className="lp-feature-desc">
                  Targeted exercises to train your auditory processing (Pro)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="lp-section" data-testid="lp-faq-section">
        <div className="lp-container">
          <h2 className="lp-h2">Frequently Asked Questions</h2>
          <div className="lp-faq-list" data-testid="lp-faq-list">
            {faqs.map((faq, i) => (
              <FaqItem key={i} item={faq} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="lp-section lp-cta-final" data-testid="lp-final-cta">
        <div className="lp-container lp-cta-center">
          <h2 className="lp-h2">Test Your Hearing in 5 Minutes</h2>
          <p className="lp-free-badge lp-free-badge--center" data-testid="lp-final-free-badge">
            Take your first test free
          </p>
          <AppStoreBadge className="lp-cta-badge" />
          <p className="lp-trust lp-trust--final">
            Free download &bull; iPhone &bull; Headphones required
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer" data-testid="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-links" data-testid="lp-footer-links">
            <a href="/hearingdecibelpro/privacy" data-testid="lp-footer-privacy">Privacy Policy</a>
            <a href="/hearingdecibelpro/terms" data-testid="lp-footer-terms">Terms of Service</a>
            <a href="/hearingdecibelpro/support" data-testid="lp-footer-support">Support</a>
          </div>
          <p className="lp-footer-copy">
            &copy; {new Date().getFullYear()} Radhesh Gowd. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
